import { NavLink, Outlet, Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { LayoutDashboard, Users, Inbox, LineChart, Mail, LogOut, ArrowLeft, Sun, Moon } from "lucide-react";

const NAV = [
  { to: "/admin", label: "Overblik", icon: LayoutDashboard, end: true },
  { to: "/admin/brugere", label: "Brugere", icon: Users },
  { to: "/admin/henvendelser", label: "Henvendelser", icon: Inbox },
  { to: "/admin/tracking", label: "Tracking", icon: LineChart },
  { to: "/admin/mandagsmail", label: "Mandagsmail", icon: Mail },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const nav = useNavigate();
  const doLogout = async () => { await logout(); nav("/login"); };

  return (
    <div className={`min-h-screen bg-background text-foreground flex ${theme === "dark" ? "dark" : ""}`}>
      <aside className="w-64 shrink-0 border-r border-border bg-card/40 hidden md:flex flex-col fixed h-screen" data-testid="admin-sidebar">
        <Link to="/admin" className="flex items-center gap-2 px-6 h-16 border-b border-border">
          <span className="font-display text-lg font-medium tracking-tight">Aicom<span style={{ color: "#1dddbf" }}>AI</span></span>
          <span className="ml-1 text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Admin</span>
        </Link>
        <nav className="flex-1 p-3 space-y-1">
          {NAV.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.end} data-testid={`admin-nav-${n.label.toLowerCase()}`}
              className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm transition-colors ${isActive ? "bg-foreground text-background font-medium" : "text-muted-foreground hover:bg-secondary hover:text-foreground"}`}>
              <n.icon className="h-4 w-4" /> {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t border-border space-y-1">
          <Link to="/" data-testid="admin-back-to-aicom" className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm text-muted-foreground hover:bg-secondary hover:text-foreground transition-colors">
            <ArrowLeft className="h-4 w-4" /> Tilbage til Aicom
          </Link>
          <button onClick={toggleTheme} className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm text-muted-foreground hover:bg-secondary hover:text-foreground transition-colors">
            {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />} {theme === "dark" ? "Lyst tema" : "Mørkt tema"}
          </button>
          <div className="px-3 py-2 rounded-xl bg-secondary/50">
            <p className="text-sm font-medium truncate" data-testid="admin-user-name">{user?.name || "Administrator"}</p>
            <p className="text-xs text-muted-foreground truncate" data-testid="admin-user-email">{user?.email}</p>
          </div>
          <button onClick={doLogout} data-testid="admin-logout" className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm text-muted-foreground hover:bg-secondary hover:text-foreground transition-colors">
            <LogOut className="h-4 w-4" /> Log ud
          </button>
        </div>
      </aside>

      <div className="flex-1 md:ml-64 min-w-0">
        <div className="md:hidden flex items-center justify-between px-4 h-14 border-b border-border sticky top-0 bg-background/90 backdrop-blur z-20">
          <span className="font-display font-medium">Aicom<span style={{ color: "#1dddbf" }}>AI</span> Admin</span>
          <button onClick={doLogout} className="text-sm text-muted-foreground">Log ud</button>
        </div>
        <div className="md:hidden flex gap-1 px-3 py-2 overflow-x-auto border-b border-border sticky top-14 bg-background z-10">
          {NAV.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.end}
              className={({ isActive }) => `whitespace-nowrap px-3 py-1.5 rounded-full text-xs ${isActive ? "bg-foreground text-background" : "bg-secondary text-muted-foreground"}`}>
              {n.label}
            </NavLink>
          ))}
        </div>
        <main className="p-5 sm:p-8 max-w-[1400px] mx-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
